import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { isPostHogEnabled } from "@/lib/posthog";
import { useAuthStore } from "@/stores/authStore";
import { useExperimentStore } from "@/stores/experimentStore";

interface PostHogIdentityProps {
  children: React.ReactNode;
}

/**
 * Component that syncs user identity with PostHog and tracks page views.
 * Must be placed inside the official PostHogProvider and AuthProvider.
 * @param props - Component props
 * @param props.children - Child components
 * @returns The children with PostHog identity syncing
 */
export function PostHogIdentity({ children }: PostHogIdentityProps) {
  const location = useLocation();
  const posthog = usePostHog();
  const { user, memberships } = useAuthStore();
  const isAuthenticated = Boolean(user);
  const { loadVariants } = useExperimentStore();

  // Load experiment variants on mount
  useEffect(() => {
    void loadVariants();
  }, [loadVariants]);

  // Identify user when authenticated
  useEffect(() => {
    if (!isPostHogEnabled()) return;

    if (isAuthenticated && user) {
      // Get driver membership info if exists
      const driverMembership = memberships.find((m) => m.roles.includes("driver"));

      posthog.identify(user.id, {
        email: user.email,
        name: user.name,
        // Include subscription context for experiment targeting
        has_company: user.companyRegistrationInfo !== null,
        is_driver: user.driverRegistrationInfo !== null,
        driver_approval_status: user.driverApprovalStatus,
        business_approval_status: user.businessApprovalStatus,
        subscription_tier: driverMembership?.subscriptionTierId ?? null,
      });

      // Reload flags with user identity for personalized experiments
      void loadVariants();
    } else {
      posthog.reset();
    }
  }, [isAuthenticated, user, memberships, loadVariants, posthog]);

  // Track page views on route change
  useEffect(() => {
    if (!isPostHogEnabled()) return;

    posthog.capture("$pageview", {
      path: location.pathname,
    });
  }, [location.pathname, posthog]);

  return <>{children}</>;
}
